import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import jszip from "jszip";
import pdfmake from "pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import DataTable from "datatables.net-react";
import DataTablesCore from "datatables.net-bs5";
import "datatables.net-buttons-bs5";
import "datatables.net-buttons/js/buttons.html5.mjs";
import "datatables.net-buttons/js/buttons.print.mjs";
import "datatables.net-responsive-bs5";

DataTablesCore.Buttons.jszip(jszip);
pdfmake.vfs = pdfFonts?.pdfMake?.vfs || pdfmake.vfs;
DataTablesCore.Buttons.pdfMake(pdfmake);
DataTable.use(DataTablesCore);

/*
  Filename:    PastVolunteers.js
  Description: Displays historical volunteer check-ins with date filtering and worked-hour calculations.
*/

const PastVolunteers = () => {
  const navigate = useNavigate();

  const [pastVolunteerEntries, setPastVolunteerEntries] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organizationName, setOrganizationName] = useState("Loading...");
  const [isRootUser, setIsRootUser] = useState(false);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editCheckedInAt, setEditCheckedInAt] = useState("");
  const [editCheckedOutAt, setEditCheckedOutAt] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const formatDateTimeForInput = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";
    
    // Format as YYYY-MM-DDTHH:MM for datetime-local input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleOpenShiftDetails = (entry) => {
    setSelectedEntry(entry);
    setEditCheckedInAt(formatDateTimeForInput(entry.checkedInAt));
    setEditCheckedOutAt(formatDateTimeForInput(entry.checkedOutAt));
    setEditNotes(entry.notes || "");
    setIsEditMode(false);
    setShowShiftModal(true);
  };

  const handleCloseShiftModal = () => {
    setShowShiftModal(false);
    setIsEditMode(false);
    setSelectedEntry(null);
    setEditCheckedInAt("");
    setEditCheckedOutAt("");
    setEditNotes("");
  };

  const handleSaveEdit = async () => {
    if (!selectedEntry) return;

    const sessionID = Cookies.get("sessionID");
    if (!sessionID) {
      navigate("/SignIn");
      return;
    }

    try {
      const updateData = {
        checkedInAt: editCheckedInAt ? new Date(editCheckedInAt).toISOString() : null,
        checkedOutAt: editCheckedOutAt ? new Date(editCheckedOutAt).toISOString() : null,
        notes: editNotes.trim(),
      };

      await axios.put(
        `${process.env.REACT_APP_BACKURL}/api/foodPantry/volunteer/${selectedEntry.checkInID}`,
        updateData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionID}`,
          },
        }
      );

      // Refresh the data
      await fetchPastVolunteers(organizationName);

      // Close modal
      handleCloseShiftModal();
    } catch (error) {
      console.error("Error updating volunteer check-in:", error);
      alert("Failed to update volunteer check-in. Please try again.");
    }
  };

  const getHoursWorked = (checkedInAt, checkedOutAt) => {
    if (!checkedInAt || !checkedOutAt) return null;

    const checkIn = new Date(checkedInAt);
    const checkOut = new Date(checkedOutAt);

    if (Number.isNaN(checkIn.getTime()) || Number.isNaN(checkOut.getTime())) {
      return null;
    }

    const diffMs = checkOut.getTime() - checkIn.getTime();
    if (diffMs < 0) return null;

    return diffMs / (1000 * 60 * 60);
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return "-";

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString();
  };

  const fetchPastVolunteers = async (currentOrgName) => {
    if (
      !currentOrgName ||
      currentOrgName === "Loading..." ||
      currentOrgName === "Select Organization" ||
      currentOrgName === "No Organization Found in cookie" ||
      currentOrgName === "Error loading organization"
    ) {
      setPastVolunteerEntries([]);
      return;
    }

    const sessionID = Cookies.get("sessionID");
    if (!sessionID) {
      navigate("/SignIn");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKURL + "/api/foodPantry/volunteer/past",
        {
          params: { orgName: currentOrgName },
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionID}`,
          },
        }
      );

      const data = response.data;
      if (data && Array.isArray(data.Volunteers)) {
        setPastVolunteerEntries(data.Volunteers);
      } else {
        setPastVolunteerEntries([]);
      }
    } catch (error) {
      console.error("Error fetching past volunteers:", error);
      setPastVolunteerEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkUserRoleAndFetchData = async () => {
      const sessionID = Cookies.get("sessionID");
      if (!sessionID) {
        navigate("/SignIn");
        return;
      }

      const fetchOrgData = async () => {
        const cookieValue = Cookies.get("OrgID");
        if (!cookieValue) {
          setOrganizationName("No Organization Found in cookie");
          return;
        }

        try {
          const orgId = cookieValue;

          const url = process.env.REACT_APP_BACKURL + `/api/foodPantry/organization/${orgId}`;
          const response = await fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch organization: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          if (data.organization && data.organization.organizationName) {
            setOrganizationName(data.organization.organizationName);
            // For non-root users, only allow their current organization in the dropdown
            setOrganizations([{
              id: orgId,
              name: data.organization.organizationName
            }]);
          } else {
            setOrganizationName("Organization name not available");
          }
        } catch (error) {
          console.error("Error fetching organization data:", error);
          setOrganizationName("Error loading organization");
        }
      };

      const fetchOrganizations = async () => {
        try {
          const response = await axios.get(
            process.env.REACT_APP_BACKURL + "/api/foodPantry/organization",
            {
              headers: {
                Authorization: `Bearer ${sessionID}`,
              },
              withCredentials: true,
            }
          );

          const data = response.data;
          if (data?.OrgNames && Array.isArray(data.OrgNames)) {
            const formattedOrganizations = data.OrgNames.map((org) => ({
              id: org.orgID,
              name: org.name,
            }));
            setOrganizations(formattedOrganizations);
            setOrganizationName("Select Organization");
          }
        } catch (error) {
          console.error("Error fetching organizations:", error);
        }
      };

      try {
        const userResponse = await axios.get(
          process.env.REACT_APP_BACKURL + "/api/foodPantry/user/role",
          {
            headers: {
              Authorization: `Bearer ${sessionID}`,
            },
            withCredentials: true,
          }
        );

        const { roleID } = userResponse.data;
        if (roleID === "0") {
          setIsRootUser(true);
          fetchOrganizations();
        } else {
          setIsRootUser(false);
          fetchOrgData();
        }
      } catch (error) {
        console.error("Error checking user role:", error);
        navigate("/SignIn");
      }
    };

    checkUserRoleAndFetchData();
  }, [navigate]);

  useEffect(() => {
    fetchPastVolunteers(organizationName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationName]);

  const filteredEntries = useMemo(() => {
    const fromBoundary = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const toBoundary = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;
    const searchValue = searchText.trim().toLowerCase();

    return pastVolunteerEntries.filter((entry) => {
      const checkInDate = entry.checkedInAt ? new Date(entry.checkedInAt) : null;

      if (fromBoundary && checkInDate && checkInDate < fromBoundary) return false;
      if (toBoundary && checkInDate && checkInDate > toBoundary) return false;

      if (!searchValue) return true;

      const fullName = `${entry.firstName || ""} ${entry.lastName || ""}`.trim().toLowerCase();
      const email = (entry.email || "").toLowerCase();
      return fullName.includes(searchValue) || email.includes(searchValue);
    });
  }, [pastVolunteerEntries, dateFrom, dateTo, searchText]);

  const totalCompletedHours = useMemo(() => {
    return filteredEntries.reduce((sum, entry) => {
      const hours = getHoursWorked(entry.checkedInAt, entry.checkedOutAt);
      return sum + (hours || 0);
    }, 0);
  }, [filteredEntries]);

  const tableData = useMemo(() => {
    return filteredEntries.map((entry) => {
      const fullName = `${entry.firstName || ""} ${entry.lastName || ""}`.trim() || "-";
      const hoursWorked = getHoursWorked(entry.checkedInAt, entry.checkedOutAt);
      return {
        ...entry,
        volunteerName: fullName,
        emailDisplay: entry.email || "-",
        checkInDisplay: formatDateTime(entry.checkedInAt),
        checkOutDisplay: formatDateTime(entry.checkedOutAt),
        hoursDisplay: hoursWorked !== null ? `${hoursWorked.toFixed(2)} hrs` : "In Progress",
      };
    });
  }, [filteredEntries]);

  const columnsVolunteerHours = [
    {
      title: "Volunteer",
      data: "volunteerName",
      render: function (data) {
        const safeName = String(data || "-");
        const initials = safeName
          .split(" ")
          .filter(Boolean)
          .map((namePart) => namePart[0])
          .join("")
          .slice(0, 2)
          .toUpperCase();

        return `
          <div class="d-flex align-items-center">
            <div class="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center me-2" style="width: 32px; height: 32px;">
              <span class="text-white fw-bold" style="font-size: 12px;">${initials || "-"}</span>
            </div>
            ${safeName}
          </div>
        `;
      },
    },
    { title: "Check In", data: "checkInDisplay" },
    { title: "Check Out", data: "checkOutDisplay" },
    { title: "Hours Worked", data: "hoursDisplay" },
  ];

  return (
    <React.Fragment>
      <style>
        {`
          #past-volunteers-table-container table.dataTable,
          #past-volunteers-table-container table.dataTable thead th,
          #past-volunteers-table-container table.dataTable tbody td,
          #past-volunteers-table-container .dataTables_wrapper,
          #past-volunteers-table-container .dataTables_info,
          #past-volunteers-table-container .dataTables_filter,
          #past-volunteers-table-container .dataTables_length,
          #past-volunteers-table-container .dataTables_paginate {
            color: #000 !important;
          }
        `}
      </style>
      <div className="card col-12 col-md-10 offset-0 offset-md-1 mt-5 card-body container my-3">
        <div className="card-header d-flex justify-content-between align-items-center pb-3 flex-wrap gap-2">
          <h3 className="mb-0">
            <i className="bi bi-clock-history pe-3"></i>
            <span>
              Volunteer Hours
            </span>
          </h3>

          {isRootUser ? (
            <select
              className="form-select w-auto"
              value={organizationName}
              onChange={(e) => {
                // Only root users can change organizations
                if (isRootUser) {
                  const selectedName = e.target.value;
                  setOrganizationName(selectedName);
                }
              }}
            >
              <option value="Select Organization">Select Organization</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.name}>
                  {org.name}
                </option>
              ))}
            </select>
          ) : (
            <div className="d-flex align-items-center p-2 rounded border bg-light">
              <h5 className="mb-0 me-3">
                <span className="fw-bold">Organization:</span> {organizationName}
              </h5>
            </div>
          )}
        </div>

        <div className="row g-3 mt-2 align-items-end">
          <div className="col-12 col-md-4">
            <label className="form-label mb-1">Volunteer Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Name or email"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <div className="col-6 col-md-3">
            <label className="form-label mb-1">Date From</label>
            <input
              type="date"
              className="form-control"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="col-6 col-md-3">
            <label className="form-label mb-1">Date To</label>
            <input
              type="date"
              className="form-control"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          <div className="col-12 col-md-2">
            <button
              type="button"
              className="btn btn-primary w-100"
              onClick={() => {
                setSearchText("");
                setDateFrom("");
                setDateTo("");
              }}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3 mb-2 flex-wrap gap-2">
          <div>
            <span className="fw-semibold">Entries:</span> {filteredEntries.length}
          </div>
          <div>
            <span className="fw-semibold">Total Worked Hours:</span> {totalCompletedHours.toFixed(2)} hrs
          </div>
        </div>

        <div className="mb-2">
          <small className="text-muted">
            <i className="fa fa-info-circle me-1"></i>
            Click on any volunteer shift row to view details.
          </small>
        </div>

        <div id="past-volunteers-table-container">
          {loading ? (
            <div className="text-center py-4">Loading past volunteers...</div>
          ) : (
            <DataTable
              data={tableData}
              columns={columnsVolunteerHours}
              options={{
                paging: true,
                pageLength: 15,
                scrollX: true,
                searching: false,
                ordering: true,
                order: [[1]],
                responsive: false,
                dom: "Bfrtip",
                buttons: [
                  {
                    extend: "collection",
                    text: "Export",
                    buttons: [
                      {
                        extend: "excel",
                        text: "Export to Excel",
                      },
                      {
                        extend: "pdf",
                        text: "Export to PDF",
                      },
                      {
                        extend: "print",
                        text: "Print",
                      },
                    ],
                  },
                ],
                language: {
                  emptyTable: "No volunteer records found for the selected filters.",
                  info: "_START_-_END_ of _TOTAL_ volunteer shifts",
                  infoEmpty: "No volunteer shifts available",
                  infoFiltered: "(filtered from _MAX_ total shifts)",
                },
                rowCallback: function (row, data) {
                  row.style.cursor = "pointer";
                  row.onclick = function () {
                    handleOpenShiftDetails(data);
                  };
                },
                initComplete: function () {
                  const buttonsContainer = document.querySelector(".dt-buttons");
                  if (buttonsContainer) {
                    buttonsContainer.classList.add("d-flex", "gap-2");
                  }
                  const api = this.api && typeof this.api === 'function' ? this.api() : null;

                // Attach buttons styling and intro attributes 
                setTimeout(() => {
                  const buttonsContainer = document.querySelector('.dt-buttons');
                  if (buttonsContainer) {
                    buttonsContainer.classList.add('d-flex', 'gap-3');
                    // Add Intro.js attributes to the button container
                    buttonsContainer.setAttribute('data-step', '3');
                    // Add data-step to individual buttons for tour
                    const btns = buttonsContainer.querySelectorAll('button, .btn');
                    // Receive
                    const receiveBtn = Array.from(btns).find(btn => btn.innerHTML.includes('fa-arrow-down'));
                    if (receiveBtn) receiveBtn.setAttribute('data-step', 'receive-items');
                    // Release
                    const releaseBtn = Array.from(btns).find(btn => btn.innerHTML.includes('fa-arrow-up'));
                    if (releaseBtn) releaseBtn.setAttribute('data-step', 'release-items');
                    // Create
                    const createBtn = Array.from(btns).find(btn => btn.innerHTML.includes('fa-plus'));
                    if (createBtn) createBtn.setAttribute('data-step', 'create-modal');
                  }
                }, 0);

                if (!api) return;

                // Safe adjust helper: wrap in RAF and try/catch
                const safeAdjust = () => {
                  try {
                    if (typeof window.requestAnimationFrame === 'function') {
                      window.requestAnimationFrame(() => {
                        try { api.columns.adjust(); } catch (err) { /* swallow */ }
                      });
                    } else {
                      api.columns.adjust();
                    }
                  } catch (e) {
                    /* swallow */
                  }
                };

                // Debounce helper
                let resizeTimer = null;
                const scheduleAdjust = (delay = 100) => {
                  if (resizeTimer) clearTimeout(resizeTimer);
                  resizeTimer = setTimeout(() => {
                    safeAdjust();
                    resizeTimer = null;
                  }, delay);
                };

                // Use ResizeObserver on the table container, compare width to avoid needless adjusts
                const container = document.getElementById('past-volunteers-table-container') || document.body;
                let lastWidth = container ? Math.round(container.getBoundingClientRect().width) : null;

                if (typeof ResizeObserver !== 'undefined') {
                  const ro = new ResizeObserver(entries => {
                    for (const entry of entries) {
                      const w = Math.round(entry.contentRect.width);
                      if (w !== lastWidth) {
                        lastWidth = w;
                        scheduleAdjust(120);
                      }
                    }
                  });
                  try { ro.observe(container); } catch (e) { /* ignore if observe fails */ }

                  // cleanup on destroy
                  api.on && api.on('destroy', () => {
                    try { ro.disconnect(); } catch (e) {}
                    if (resizeTimer) { clearTimeout(resizeTimer); resizeTimer = null; }
                  });
                } else {
                  // fallback: debounced window resize
                  const onWinResize = () => scheduleAdjust(150);
                  window.addEventListener('resize', onWinResize);
                  api.on && api.on('destroy', () => {
                    window.removeEventListener('resize', onWinResize);
                    if (resizeTimer) { clearTimeout(resizeTimer); resizeTimer = null; }
                  });
                }

                // initial adjust after init
                scheduleAdjust(50);
                },
              }}

            />
          )}
        </div>
      </div>

      <Modal show={showShiftModal} onHide={handleCloseShiftModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-clock-history me-2"></i>
            {isEditMode ? "Edit Volunteer Shift" : "Volunteer Shift Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEntry && (
            <div>
              <div className="mb-3">
                <h6 className="fw-bold">
                  Volunteer: {`${selectedEntry.firstName || ""} ${selectedEntry.lastName || ""}`.trim()}
                </h6>
                <p className="text-muted mb-0">Email: {selectedEntry.email || "-"}</p>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Check In Date & Time</label>
                  {isEditMode ? (
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={editCheckedInAt}
                      onChange={(e) => setEditCheckedInAt(e.target.value)}
                    />
                  ) : (
                    <div className="form-control bg-light">{formatDateTime(selectedEntry.checkedInAt)}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Check Out Date & Time</label>
                  {isEditMode ? (
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={editCheckedOutAt}
                      onChange={(e) => setEditCheckedOutAt(e.target.value)}
                    />
                  ) : (
                    <div className="form-control bg-light">{formatDateTime(selectedEntry.checkedOutAt)}</div>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">Shift Notes</label>
                  {isEditMode ? (
                    <textarea
                      className="form-control"
                      rows="4"
                      placeholder="Enter notes about this shift..."
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                    />
                  ) : (
                    <div className="form-control bg-light" style={{ minHeight: "96px", whiteSpace: "pre-wrap" }}>
                      {selectedEntry.notes || "No notes for this shift."}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {isEditMode ? (
            <>
              <Button variant="secondary" onClick={() => setIsEditMode(false)}>
                Back to Details
              </Button>
              <Button variant="primary" onClick={handleSaveEdit}>
                <i className="bi bi-save me-1"></i>
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button className="btn btn-primary" onClick={() => setIsEditMode(true)}>
                <i className="bi bi-pencil-square me-1"></i>
                Edit Shift
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default PastVolunteers;
